import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { green, grey, yellow } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SplashPage } from "contexts/Subscription";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  BentoCategory,
  OrganizationNiche,
  OrganizationNicheStatus,
} from "schemas/dashboard";
import { EmailSettingTemplate, EmailSettings } from "schemas/email";
import { CustomEvent } from "schemas/functions";

import { Template } from "features/Influencer/ContactList/schema";
import VAProgressBar from "features/Influencer/Discover/Results/Banner/VAProgressBar";
import {
  BulkSendSettingsStatus,
  OrganizationBulkSendSettings,
} from "features/Influencer/Discover/Results/DiscoverFilter/AutomationVA/UpcomingPitchScheduleDialog/schema";
import { fetcherAuth } from "utils/api";
import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";
import { getSource, trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import EmailBoost from "../EmailBoost";
import EditNicheDialog from "./EditNicheDialog";
import EditTemplateDialog from "./EditTemplateDialog";

interface IProps {
  bulkSendSetting: OrganizationBulkSendSettings | null;
  setBulkSendSetting: Dispatch<
    SetStateAction<OrganizationBulkSendSettings | null>
  >;
  fallbackTemplates: Template[];
  setFallbackTemplates: Dispatch<SetStateAction<Template[]>>;
  bulkSendEmailSetting: number | null;
  categoriesToPickFrom: BentoCategory[];
}

export default function OldVASettings({
  bulkSendSetting,
  setBulkSendSetting,
  fallbackTemplates,
  setFallbackTemplates,
  bulkSendEmailSetting,
  categoriesToPickFrom,
}: IProps) {
  const location = useLocation();
  const { handleCheckout } = useSubscription();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isAdmin = Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN));

  // Contexts
  const { currentUser, currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { templates, userHasNoTemplates } = useContext(
    OutreachTemplatesContext,
  );

  // States
  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [niches, setNiches] = useState<OrganizationNiche[]>([]);
  const [nicheTemplates, setNicheTemplates] = useState<(Template[] | null)[]>(
    [],
  );
  const [allEmailSettings, setAllEmailSettings] = useState<EmailSettings[]>([]);
  const [editNicheId, setEditNicheId] = useState<number | null>(null);
  const [editEmailSettingId, setEditEmailSettingId] = useState<
    number | null | undefined
  >(null);
  const [editEmailSettingTemplates, setEditEmailSettingTemplates] = useState<
    Template[] | null
  >(null);
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const [displayRequestButton, setDisplayRequestButton] =
    useState<boolean>(false);

  const getNiches = async () => {
    try {
      const { niches } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches`,
        "GET",
      );
      setNiches(niches);
      return niches;
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const updateSettings = async () => {
    setSaveLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bulk-send-settings/${bulkSendSetting?.id}`,
        "PATCH",
        {},
        {
          status: bulkSendSetting?.status,
          personalizeOutreach: bulkSendSetting?.personalizeOutreach,
        },
      );
      if (
        bulkSendSetting?.status === BulkSendSettingsStatus.inactive &&
        !isAdmin
      ) {
        setDisplayRequestButton(true);
      }
      setAlert("Successfully saved settings", "success");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSendRequest = async () => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bulk-send-settings/request-resume`,
        "POST",
      );
      setAlert(
        "Your request has been sent. We will review your account and notify you once outreach has been resumed.",
        "success",
      );
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-settings`,
        "GET",
      );
      setAllEmailSettings(res.emailSettings);
      return res.emailSettings;
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const computeNicheTemplates = (
    nichesRes: OrganizationNiche[],
    emailSettingsRes: EmailSettings[],
  ) => {
    const templatesById = new Map<number, Template>();
    for (const template of templates) {
      if (template.id) {
        templatesById.set(template.id, template);
      }
    }

    const emailSettingIdToTemplateIds = new Map<number, number[]>();
    for (const s of emailSettingsRes ?? []) {
      const templateIds = s.emailSettingTemplates?.map(
        (settingTemplate: EmailSettingTemplate) => settingTemplate.templateId,
      );
      if (s.id != null && templateIds != null) {
        emailSettingIdToTemplateIds.set(s.id, templateIds);
      }
    }

    const computedNicheTemplates: (Template[] | null)[] = (nichesRes ?? []).map(
      (niche: OrganizationNiche) => {
        const templateIds = niche.emailSettingId
          ? emailSettingIdToTemplateIds.get(niche.emailSettingId)
          : undefined;
        if (!templateIds) return null;
        return templateIds
          .map((templateId) => templatesById.get(templateId))
          .filter((t): t is Template => t !== undefined);
      },
    );

    setNicheTemplates(computedNicheTemplates);
  };

  const loadData = async () => {
    setFetchLoading(true);
    try {
      const [nichesRes, emailSettingsRes] = await Promise.all([
        getNiches(),
        fetchEmailSettings(),
      ]);
      computeNicheTemplates(nichesRes, emailSettingsRes);
    } finally {
      setFetchLoading(false);
    }
  };

  const openVaBundledPage = () => {
    trackEvent("Add A Niche Button Clicked");

    handleCheckout(
      undefined,
      location.pathname,
      getSource(),
      SplashPage.va_bundled,
    );
  };

  useEffect(() => {
    if ((templates.length > 0 || userHasNoTemplates) && !isFetched) {
      loadData();
      setIsFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length, userHasNoTemplates]);

  const editNicheIndex = editNicheId
    ? niches.findIndex((niche) => niche.id === editNicheId)
    : -1;
  const editTemplates = nicheTemplates[editNicheIndex] ?? null;
  const editNiche = niches[editNicheIndex] ?? null;

  useEffect(() => {
    if (
      !isAdmin &&
      bulkSendSetting?.status === BulkSendSettingsStatus.inactive
    ) {
      setDisplayRequestButton(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (fetchLoading) {
    return (
      <Grid container sx={{ p: 2 }}>
        <Grid item container xs={12}>
          <Grid item xs={12} md={12} lg={8} container>
            <Grid item xs={8}>
              <Skeleton height="80px" />
            </Grid>
            <Grid item container xs={12}>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
            </Grid>
            <Grid item container xs={12} columnSpacing={1}>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
            </Grid>
            <Grid item container xs={12} columnSpacing={1}>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
            </Grid>
            <Grid item container xs={12} columnSpacing={1}>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
              <Grid item xs={4}>
                <Skeleton height="80px" />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Box sx={{ p: 2, pb: { xs: 10, md: 2 } }}>
      <VAProgressBar />
      <Typography variant="h5" paragraph>
        {currentUser?.name}, Welcome to Your Virtual Assistant Dashboard
      </Typography>
      <Typography variant="h6" sx={{ fontSize: "15px" }} paragraph>
        Your Niches
      </Typography>
      <Grid container gap={2}>
        <Grid item xs={12} md={12} lg={10} container gap={1}>
          <Grid item container xs={12}>
            <Grid item xs={5} md={3}>
              <Typography variant="caption">Niche</Typography>
            </Grid>
            <Grid item xs={5} md={4}>
              <Typography variant="caption">Template</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption">Status</Typography>
            </Grid>
          </Grid>
          {niches.map((niche, idx) => (
            <Grid
              key={niche.id}
              container
              item
              xs={12}
              spacing={1}
              alignItems="center"
            >
              <Grid item xs={5} md={3}>
                <Chip
                  size="small"
                  sx={{
                    backgroundColor: "#A3E4D7",
                    fontSize: 12,
                    borderRadius: 2,
                    color: grey[800],
                    mr: 1,
                    textTransform: "capitalize",
                  }}
                  label={niche.name}
                />
              </Grid>
              <Grid item xs={5} md={4}>
                <Chip
                  size="small"
                  sx={{
                    backgroundColor: nicheTemplates[idx]?.[0]?.name
                      ? "#D6EAF8"
                      : grey[200],
                    fontSize: 12,
                    borderRadius: 2,
                    color: grey[800],
                    maxWidth: "calc(100% - 40px)",
                    mr: 1,
                    textTransform: "capitalize",
                  }}
                  label={nicheTemplates[idx]?.[0]?.name || "Setting Up"}
                />
                {nicheTemplates[idx]?.[0] && niche.emailSettingId && (
                  <Button
                    size="small"
                    color="inherit"
                    sx={{ textTransform: "none" }}
                    disabled={niche.status === OrganizationNicheStatus.pending}
                    onClick={() => {
                      setEditEmailSettingId(niche.emailSettingId);
                      setEditEmailSettingTemplates(nicheTemplates[idx]);
                      trackEvent(
                        "VA Updates Page Niche Templates Edit Icon Clicked",
                        {
                          "Niche Name": niche.name,
                          "Niche Id": niche.id,
                          "Template Name": nicheTemplates[idx]?.[0]?.name,
                        },
                      );
                    }}
                  >
                    Edit{" "}
                    <Box
                      component="i"
                      sx={{ fontSize: 14, ml: 1 }}
                      className="fa-regular fa-pen"
                    />
                  </Button>
                )}
              </Grid>
              <Grid item xs={2}>
                <Chip
                  size="small"
                  sx={{
                    backgroundColor:
                      niche?.status === OrganizationNicheStatus.pending
                        ? grey[200]
                        : green[200],
                    fontSize: 12,
                    borderRadius: 2,
                    color: grey[800],
                    mr: 1,
                    textTransform: "capitalize",
                  }}
                  label={niche?.status}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                sx={{
                  borderBottom: { xs: `1px solid ${grey[300]}`, md: "0px" },
                  pb: { xs: 1 },
                  mb: { xs: 1 },
                }}
              >
                <Tooltip
                  title={
                    !niche.canEdit
                      ? "This niche was created in the last 30 days. You must wait 30 days before being able to change this niche"
                      : niche.status === OrganizationNicheStatus.pending
                        ? "You cannot edit a niche while it is in pending status and is still being set up"
                        : null
                  }
                  enterTouchDelay={0}
                >
                  <Box>
                    <Button
                      size="small"
                      color="inherit"
                      variant={isSmallScreen ? "outlined" : "text"}
                      disabled={
                        niche.status === OrganizationNicheStatus.pending ||
                        !niche.canEdit
                      }
                      sx={{ textTransform: "none", my: { xs: 1, md: 0 } }}
                      onClick={() => {
                        setEditNicheId(niche.id!);
                        trackEvent(
                          "VA Updates Page Niche Change Button Clicked",
                          {
                            "Niche Name": niche.name,
                            "Niche Id": niche.id,
                          },
                        );
                      }}
                    >
                      Change niche{" "}
                      <Box
                        component="i"
                        className="fa-solid fa-rotate-left"
                        sx={{ ml: 1 }}
                      />
                    </Button>
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>
          ))}
        </Grid>
        <Grid item xs={12}>
          <Button
            onClick={openVaBundledPage}
            size="small"
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            + increase email volume{" "}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="textSecondary">
            <i>
              Note: After changing your niche, you must wait 30 days before
              making another change.
            </i>
          </Typography>
        </Grid>
      </Grid>
      <Grid container gap={2}>
        <Grid item xs={12} md={8} lg={6} container gap={1}>
          {fallbackTemplates[0]?.name && (
            <>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: "15px", mt: 2 }}
                  gutterBottom
                >
                  General Use Template
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  This is the <strong>default outreach template</strong> used
                  when a brand doesn't fall into any of the specific niches
                  listed above. For example, if you save a brand for your
                  Virtual Assistant to contact and it doesn't match any of the
                  predefined niches, this general template will automatically be
                  used.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Chip
                  size="small"
                  sx={{
                    backgroundColor: fallbackTemplates[0]?.name
                      ? "#D6EAF8"
                      : grey[200],
                    fontSize: 12,
                    borderRadius: 2,
                    color: grey[800],
                    mr: 1,
                    textTransform: "capitalize",
                  }}
                  label={fallbackTemplates[0]?.name || "Setting Up"}
                />
                <Button
                  size="small"
                  color="inherit"
                  sx={{ textTransform: "none" }}
                  onClick={() => {
                    setEditEmailSettingId(bulkSendEmailSetting);
                    setEditEmailSettingTemplates(fallbackTemplates);
                    trackEvent(
                      "VA Updates Page Fallback Templates Edit Icon Clicked",
                      {
                        "Template Name": fallbackTemplates[0]?.name,
                      },
                    );
                  }}
                >
                  Edit
                  <Box
                    component="i"
                    sx={{ fontSize: 14, ml: 1 }}
                    className="fa-regular fa-pen"
                  />
                </Button>
              </Grid>
            </>
          )}

          <Grid item xs={12} sx={{ mt: 6 }}>
            <Typography variant="h6" gutterBottom>
              Settings
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkSendSetting?.personalizeOutreach}
                  onChange={(e) =>
                    setBulkSendSetting((prev) =>
                      prev
                        ? {
                            ...prev,
                            personalizeOutreach: e.target.checked,
                          }
                        : null,
                    )
                  }
                  icon={<Box component="i" className="fa-regular fa-square" />}
                  checkedIcon={
                    <Box component="i" className="fa-solid fa-check-square" />
                  }
                />
              }
              label="Use AI to personalize my outreach emails"
            />

            <FormHelperText sx={{ color: grey[700] }}>
              Your emails will be automatically personalized to better match
              each brand's industry, improving relevance and reducing
              mismatches.{" "}
              <Box
                component="span"
                sx={{ color: yellow[800] }}
                className="fa-solid fa-triangle-exclamation"
              />{" "}
              However, AI may occasionally include inaccurate details or make
              the email sound less natural than your original template.
            </FormHelperText>

            {!displayRequestButton && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        bulkSendSetting?.status ===
                        BulkSendSettingsStatus.inactive
                      }
                      onChange={(e) =>
                        setBulkSendSetting((prev) =>
                          prev
                            ? {
                                ...prev,
                                status: e.target.checked
                                  ? BulkSendSettingsStatus.inactive
                                  : BulkSendSettingsStatus.active,
                              }
                            : null,
                        )
                      }
                    />
                  }
                  label="Pause Brand Outreach (Account Remains Active)"
                />
                <FormHelperText sx={{ color: grey[700] }}>
                  Once paused, your Virtual Assistant will stop sending new
                  outreach messages. Scheduled follow-ups will still go out as
                  planned. Your account and progress remain active — you can
                  restart outreach anytime.
                </FormHelperText>
              </>
            )}

            <LoadingButton
              variant="contained"
              disableElevation
              sx={{ mt: 2 }}
              onClick={updateSettings}
              loading={saveLoading}
            >
              Save Settings
            </LoadingButton>
          </Grid>

          {displayRequestButton && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ fontSize: "15px" }} gutterBottom>
                Resume Outreach
              </Typography>

              <FormHelperText sx={{ color: grey[700] }}>
                Your current outreach is <strong>paused</strong>. To resume,
                click the button below, and we will review your account. We will
                notify you once outreach has been resumed.
              </FormHelperText>

              <Button
                sx={{ mt: 1, mb: 3 }}
                onClick={handleSendRequest}
                variant="contained"
                disableElevation
              >
                Send Request
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{ fontSize: "15px", mt: 1 }}
              gutterBottom
            >
              Want Pay Info on a Brand?
            </Typography>
            <Typography paragraph>
              Forward any conversation you're having with a brand about rates to{" "}
              <strong>negotiations@onbento.com</strong>, and Bento will send you
              (anonymous) pay data we have about the brand to help you negotiate
              the best rates!
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontSize: "15px", mt: 1 }}
              gutterBottom
            >
              Want to stay updated?
            </Typography>
            <Typography paragraph>
              Want a heads-up when your pitches are scheduled each week?
              Download our app to stay in the loop.
            </Typography>
            <Typography paragraph>
              👉 Download our app on{" "}
              <Link
                href="https://apps.apple.com/us/app/bento-virtual-assistant/id6738798823"
                target="_blank"
                rel="noopener noreferrer"
              >
                iOS
              </Link>{" "}
              or{" "}
              <Link
                href="https://play.google.com/store/apps/details?id=com.onbento.bento"
                target="_blank"
              >
                Android
              </Link>
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "15px" }} gutterBottom>
              Need any other help?
            </Typography>
            <Typography paragraph>
              You get a concierge email dedicated only to VA users. Email us at{" "}
              <strong>
                <a href="mailto:va@onbento.com">va@onbento.com</a>
              </strong>{" "}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      {editNiche && (
        <EditNicheDialog
          niche={editNiche}
          templates={editTemplates}
          categoriesToPickFrom={categoriesToPickFrom}
          handleClose={() => {
            setEditNicheId(null);
          }}
          updateNiche={(newNiche) => {
            setNiches((prev) => {
              return prev.map((niche) => {
                if (niche.id === editNiche.id) {
                  return newNiche;
                }
                return niche;
              });
            });
          }}
        />
      )}

      {editEmailSettingId && editEmailSettingTemplates && (
        <EditTemplateDialog
          emailSettingId={editEmailSettingId}
          templates={editEmailSettingTemplates}
          handleClose={(e?: CustomEvent) => {
            e?.stopPropagation();
            setEditEmailSettingId(null);
            setEditEmailSettingTemplates(null);
          }}
          updateEmailSettings={(
            newEmailSetting: EmailSettings,
            newTemplates: Template[],
          ) => {
            const newEmailSettings = allEmailSettings.map((emailSetting) => {
              if (emailSetting.id === editEmailSettingId) {
                return newEmailSetting;
              }
              return emailSetting;
            });
            setAllEmailSettings(newEmailSettings);
            computeNicheTemplates(niches, newEmailSettings);
            if (editEmailSettingId === bulkSendEmailSetting) {
              setFallbackTemplates(newTemplates);
            }
          }}
        />
      )}

      <EmailBoost />
    </Box>
  );
}
